import React from 'react'
import ReactGA from 'react-ga'
import Checkbox from '@material-ui/core/Checkbox'
import FormControl from '@material-ui/core/FormControl'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import Radio from '@material-ui/core/Radio'
import Select from '@material-ui/core/Select'
import { SelectInputProps } from '@material-ui/core/Select/SelectInput'
import { withStyles } from '@material-ui/core/styles'
import { ClassNameMap } from '@material-ui/core/styles/withStyles'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'

import { FilterData } from '../../constants/Filter'
import StyledTooltip from '../StyledTooltip'

import './Filter.css'

const StyledOptions = withStyles(() => ({
  root: {
    color: '#4C4D68',
    '&$checked': {
      color: '#D708B3',
    },
  },
  checked: {},
}))

const StyledCheckbox = StyledOptions(Checkbox)
const StyledRadio = StyledOptions(Radio)

const StyledMenuItem = withStyles(() => ({
  root: {
    '&.Mui-selected': {
      backgroundColor: 'inherit',
    },
  },
}))(MenuItem)

export interface FilterProps {
  disabled?: boolean
  label?: string
  multiple?: boolean
  filterValue?: string[]
  onChange: React.Dispatch<React.SetStateAction<string[]>>
  styles?: ClassNameMap
  filterData?: Array<FilterData>
  tooltip?: boolean
  toolTipContent?: React.ReactFragment
  dataTestId?: string
  filterBase?: string[]
  disabledOptions?: { baseValue: string; disabled: string[] }[] | null
}

export const Filter: React.FC<FilterProps> = ({
  disabled = false,
  label = '',
  multiple = false,
  filterValue = [],
  onChange,
  styles,
  filterData = [],
  tooltip = false,
  toolTipContent = false,
  dataTestId = '',
  filterBase = [],
  disabledOptions = null,
}) => {
  const renderValue = (selected: string[], value: string, data: Array<FilterData>) => {
    if (selected.length > 1) {
      return 'Multiple selected'
    }

    for (let i = 0; i < data.length; i++) {
      if (data[i].value === selected[0]) {
        return data[i].label
      }
    }

    return `Select ${value.toLowerCase()}`
  }

  const onChangeFilter: SelectInputProps['onChange'] = (event) => {
    const value = typeof event.target.value === 'string' ? [event.target.value] : (event.target.value as string[])

    if (value.length) {
      onChange(
        value.sort((val1, val2) => {
          return val1.localeCompare(val2, 'en', { sensitivity: 'base' })
        }),
      )
    }
  }

  const disabledFilterOptions: Array<string> = !disabledOptions
    ? []
    : filterData.reduce((accu, item) => {
        const disabledOptionsMatchingBase = disabledOptions.find((d) => filterBase.includes(d.baseValue))
        const isItemDisabled = disabledOptionsMatchingBase?.disabled.some((d) => d === item.value)

        if (isItemDisabled) {
          accu.push(item.value)
          return accu
        }

        return accu
      }, [] as string[])

  const fireGAEvent = (eventAction: string, eventLabel: string) => {
    ReactGA.event({
      category: 'Refinance Index',
      action: eventAction,
      label: eventLabel,
    })
  }

  return (
    <FormControl variant="outlined" className={styles?.formControl} disabled={disabled}>
      <label htmlFor={`filter-select-${label.toLocaleLowerCase().replace(/\s/g, '')}`}>
        {label}
        {tooltip && toolTipContent && <StyledTooltip label={label} toolTipContent={toolTipContent} />}
      </label>
      <Select
        id={`filter-select-${label.toLowerCase().replace(/\s/g, '')}`}
        displayEmpty
        multiple={multiple}
        className={styles?.selectInput}
        data-testid={dataTestId}
        renderValue={(selected) => renderValue(selected as string[], label, filterData)}
        value={filterValue}
        onChange={onChangeFilter}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
        IconComponent={KeyboardArrowDownIcon}
      >
        {filterData.length &&
          filterData.map((data) => (
            <StyledMenuItem
              data-testid={`${data.value}-filter-menu-item`}
              key={data.id}
              value={data.value}
              selected
              disabled={disabledFilterOptions.includes(data.value)}
              onClick={() => {
                if (filterValue.indexOf(data.value) === -1) {
                  fireGAEvent(label, data.value)
                }
              }}
            >
              {multiple ? (
                <StyledCheckbox checked={filterValue.indexOf(data.value) > -1} data-testid="checkbox" />
              ) : (
                <StyledRadio checked={filterValue.indexOf(data.value) > -1} data-testid="radiobox" />
              )}

              <ListItemText className="filter-item" primary={data.label} data-testid="item" />
            </StyledMenuItem>
          ))}
      </Select>
    </FormControl>
  )
}
